/**
 * FlutterBridge - Flutter WebView ile iletişim için yardımcı fonksiyonlar
 * 
 * Bu modül, web uygulamasının Flutter WebView içinde çalışıp çalışmadığını
 * tespit eder ve Flutter ile JavaScript arasında köprü kurarak iletişim sağlar.
 */

// TypeScript tip tanımlamaları
declare global {
  interface Window {
    isFlutterApp?: boolean;
    flutterAppReady?: boolean;
    FlutterBridge?: {
      postMessage: (message: string) => void;
    };
  }
}

/**
 * Uygulamanın Flutter WebView içinde çalışıp çalışmadığını kontrol eder
 */
export const isRunningInFlutter = (): boolean => {
  // Birden fazla kontrol yaparak güvenilirliği artırıyoruz
  const checks = [
    // 1. Flutter'ın set ettiği global değişken
    typeof window.isFlutterApp !== 'undefined' && window.isFlutterApp === true,
    
    // 2. localStorage kontrolü (kalıcı)
    localStorage.getItem('isFlutterApp') === 'true',
    
    // 3. URL parametresi kontrolü
    window.location.search.includes('source=flutter_app') ||
    window.location.href.includes('source=flutter_app'),
    
    // 4. Flutter bridge'in varlığı
    typeof window.FlutterBridge !== 'undefined',
    
    // 5. User agent kontrolü (Flutter WebView genellikle özel user agent kullanır)
    navigator.userAgent.includes('GTS Flutter App'),
  ];
  
  const isFlutter = checks.some(check => check);
  
  // Eğer Flutter ortamındaysak localStorage'a kaydet (kalıcılık için)
  if (isFlutter && localStorage.getItem('isFlutterApp') !== 'true') {
    localStorage.setItem('isFlutterApp', 'true');
    console.log('📱 Flutter ortamı tespit edildi ve localStorage\'a kaydedildi');
  }
  
  return isFlutter;
};

/**
 * Flutter'a mesaj gönder
 * @param action - Yapılacak işlem (logout, goBack, refresh, vb.)
 * @param data - İsteğe bağlı ek veri
 */
export const sendMessageToFlutter = (action: string, data?: Record<string, unknown>): boolean => {
  if (!isRunningInFlutter()) {
    console.log('⚠️ Flutter ortamında değiliz, mesaj gönderilemedi');
    return false;
  }
  
  try {
    const message = JSON.stringify({
      action,
      ...data,
      timestamp: Date.now(),
    });
    
    // Flutter bridge üzerinden mesaj gönder
    if (window.FlutterBridge && typeof window.FlutterBridge.postMessage === 'function') {
      window.FlutterBridge.postMessage(message);
      console.log('✅ Flutter\'a mesaj gönderildi:', action);
      return true;
    } else {
      console.log('⚠️ FlutterBridge.postMessage mevcut değil');
      return false;
    }
  } catch (error) {
    console.error('❌ Flutter\'a mesaj gönderilirken hata:', error);
    return false;
  }
};

/**
 * Flutter'a çıkış yapıldığını bildir
 * Flutter bu mesajı aldığında oturumu kapatıp login ekranına yönlendirir
 */
export const notifyFlutterLogout = (): boolean => {
  // localStorage'dan Flutter bilgisini temizle
  localStorage.removeItem('isFlutterApp');
  console.log('📱 Flutter ortam bilgisi localStorage\'dan silindi');
  
  return sendMessageToFlutter('logout');
};

/**
 * Flutter'a geri gitme isteği gönder
 */
export const notifyFlutterGoBack = (): boolean => {
  return sendMessageToFlutter('goBack');
};

/**
 * Flutter'a sayfa yenileme isteği gönder
 */
export const notifyFlutterRefresh = (): boolean => {
  return sendMessageToFlutter('refresh');
};

/**
 * Flutter'a mesaj göster isteği gönder
 */
export const showFlutterMessage = (message: string, isError: boolean = false): boolean => {
  return sendMessageToFlutter('showMessage', { message, isError });
};

/**
 * Flutter'dan token al isteği gönder
 */
export const requestFlutterToken = (): boolean => {
  return sendMessageToFlutter('getToken');
};

/**
 * URL'den Flutter token'ını al
 */
export const getFlutterTokenFromUrl = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');
  const source = urlParams.get('source');
  
  if (token) {
    console.log('✅ URL\'den Flutter token alındı');
    
    // Flutter ortamında olduğumuzu localStorage'a kaydet
    if (source === 'flutter_app') {
      localStorage.setItem('isFlutterApp', 'true');
      console.log('📱 Flutter ortamı localStorage\'a kaydedildi');
    }
    
    // Sadece token'ı URL'den kaldır (güvenlik için) - source=flutter_app kalacak
    const newUrl = window.location.href
      .replace(/[?&]token=[^&#]+/, '') // Token'ı sil
      .replace(/\?&/, '?') // ?& durumunu düzelt
      .replace(/&&/, '&'); // && durumunu düzelt
    window.history.replaceState({}, document.title, newUrl);
  }
  
  return token;
};

/**
 * Flutter bridge'in hazır olmasını bekle
 * @param timeout - Bekleme süresi (ms)
 */
export const waitForFlutterBridge = (timeout: number = 5000): Promise<boolean> => {
  return new Promise((resolve) => {
    // Zaten hazırsa hemen çöz
    if (window.flutterAppReady) {
      resolve(true);
      return;
    }
    
    // Timeout ayarla
    const timeoutId = setTimeout(() => {
      window.removeEventListener('flutterBridgeReady', handleReady);
      resolve(false);
    }, timeout);
    
    // Event listener ile bekle
    const handleReady = () => {
      clearTimeout(timeoutId);
      resolve(true);
    };
    
    window.addEventListener('flutterBridgeReady', handleReady, { once: true });
  });
};

/**
 * Flutter'dan gelen token event'ini dinle
 * @param callback - Token alındığında çağrılacak fonksiyon
 */
export const listenForFlutterToken = (callback: (token: string) => void): () => void => {
  const handleTokenReceived = (event: CustomEvent) => {
    if (event.detail && event.detail.token) {
      callback(event.detail.token);
    }
  };
  
  window.addEventListener('flutterTokenReceived', handleTokenReceived as EventListener);
  
  // Cleanup fonksiyonu döndür
  return () => {
    window.removeEventListener('flutterTokenReceived', handleTokenReceived as EventListener);
  };
};

/**
 * Flutter ortamında çıkış yap
 * Web'de normal logout yapar, Flutter'da ise Flutter'a bildirim gönderir
 */
export const handleLogoutInFlutterContext = async (
  webLogoutFunction: () => Promise<void>
): Promise<void> => {
  if (isRunningInFlutter()) {
    console.log('🚪 Flutter ortamında çıkış yapılıyor...');
    // Flutter'a çıkış bildirimi gönder
    notifyFlutterLogout();
    // Flutter tarafı oturumu kapatacak ve login ekranına yönlendirecek
  } else {
    console.log('🚪 Web ortamında çıkış yapılıyor...');
    // Normal web logout işlemi
    await webLogoutFunction();
  }
};

export default {
  isRunningInFlutter,
  sendMessageToFlutter,
  notifyFlutterLogout,
  notifyFlutterGoBack,
  notifyFlutterRefresh,
  showFlutterMessage,
  requestFlutterToken,
  getFlutterTokenFromUrl,
  waitForFlutterBridge,
  listenForFlutterToken,
  handleLogoutInFlutterContext,
};

